import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import type { ReactNode } from 'react'
import {
  getPlanRecommendationTabFund,
  type PlanRecommendationPairView
} from '../../pair/planRecommendationPair.types'

type StepsToRebalanceAccordionBodyProps = {
  pair: PlanRecommendationPairView
  isSwp: boolean
}

export function StepsToRebalanceAccordionBody({
  pair,
  isSwp
}: StepsToRebalanceAccordionBodyProps) {
  const exitingFundName = pair.exitingFund.fundName
  const enteringFundName = pair.enteringFund.fundName
  const sellAmount = pair.exitingFund.sellAmount ?? 0
  // Omit ₹ when sell is done, or when there is no redeem amount to show.
  const shouldOmitRedeemAmount =
    pair.exitingFund.executed === true || sellAmount === 0
  // SWP amount/duration live on the selected sell↔buy edge (tab fund).
  const tabFund = getPlanRecommendationTabFund(pair)
  const swpAmount = tabFund.swpAmount ?? 0
  const swpDurationInMonths = tabFund.swpDurationInMonths ?? 0
  if (isSwp) {
    return (
      <StepsToRebalanceSwpBody
        exitingFundName={exitingFundName}
        enteringFundName={enteringFundName}
        swpAmount={swpAmount}
        swpDurationInMonths={swpDurationInMonths}
      />
    )
  }

  return (
    <div className="pb-4">
      <div className="flex flex-col gap-6 text-[13px] font-[400] leading-[150%] text-[#3D3D3D]">
        <StepRow index={1}>
          {shouldOmitRedeemAmount
            ? `Redeem Lump Sum from ${exitingFundName}`
            : `Redeem ${formatRupeeLabel({ rawAmount: sellAmount })} Lump Sum from ${exitingFundName}`}
        </StepRow>
        <StepRow index={2}>
          Wait for the amount to be credited to your bank (usually in 3-4 working days)
        </StepRow>
        <StepRow index={3}>
          {`Re-invest the same amount in ${enteringFundName} as lump-sum`}
        </StepRow>
      </div>
    </div>
  )
}

export function StepsToRebalanceSwpBody({
  exitingFundName,
  enteringFundName,
  swpAmount,
  swpDurationInMonths
}: {
  exitingFundName: string
  enteringFundName: string
  swpAmount: number
  swpDurationInMonths: number
}) {
  const swpAmountLabel = formatRupeeLabel({ rawAmount: swpAmount })
  const durationLabel =
    swpDurationInMonths === 1 ? '1 month' : `${swpDurationInMonths} months`

  return (
    <div className="pb-4">
      <div className="flex flex-col gap-6 text-[13px] font-[400] leading-[150%] text-[#3D3D3D]">
        <StepRow index={1}>
          <div>
            <div>{`Set up a monthly SWP (Systematic Withdrawal Plan) from ${exitingFundName}`}</div>
            <div className="mt-1">
              <div>{`Amount: ${swpAmountLabel}/month`}</div>
              <div>{`Duration: ${durationLabel}`}</div>
            </div>
          </div>
        </StepRow>
        <StepRow index={2}>
          {`Once each SWP is credited to your bank, reinvest the same amount in ${enteringFundName} as Lump Sum`}
        </StepRow>
      </div>
      <div className="mt-6 text-[12px] font-[400] leading-[150%] text-[#6A6E6E]">
        Note: SWP can be set up via your broker&apos;s app or website.
      </div>
    </div>
  )
}

export function StepRow({
  index,
  children
}: {
  index: number
  children: ReactNode
}) {
  return (
    <div className="flex gap-3 items-start">
      <div className="h-8 w-8 shrink-0 flex items-center justify-center">
        <StepRowBadge index={index} />
      </div>
      <div className="self-center">{children}</div>
    </div>
  )
}

export function StepRowBadge({ index }: { index: number }) {
  if (index === 1) {
    return <FirstStepRowIcon />
  }
  if (index === 2) {
    return <SecondStepRowIcon />
  }
  if (index === 3) {
    return <ThirdStepRowIcon />
  }
  if (index === 4) {
    return <FourthStepRowIcon />
  }
  if (index >= 5) {
    return <GenericStepRowIcon index={index} />
  }
  return null
}

export function FirstStepRowIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden>
      <path
        d="M12.2247 15.6397V8.875C12.2247 9.80902 11.5433 10.5662 10.7027 10.5662H10.1953"
        stroke="#3D3D3D"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.2541 15.625H10.1953"
        stroke="#3D3D3D"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.75 12.25C0.75 15.3 1.9616 18.2251 4.11827 20.3817C6.27494 22.5384 9.20001 23.75 12.25 23.75C15.3 23.75 18.2251 22.5384 20.3817 20.3817C22.5384 18.2251 23.75 15.3 23.75 12.25C23.75 9.20001 22.5384 6.27494 20.3817 4.11827C18.2251 1.9616 15.3 0.75 12.25 0.75C9.20001 0.75 6.27494 1.9616 4.11827 4.11827C1.9616 6.27494 0.75 9.20001 0.75 12.25Z"
        stroke="#F0F4F7"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function SecondStepRowIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden>
      <path
        d="M14.2541 15.6397H10.1953V14.6788C10.1953 14.0069 10.5533 13.3987 11.1073 13.1294L13.3658 12.0315C13.9054 11.7692 14.2541 11.1768 14.2541 10.5223C14.2541 9.61252 13.5904 8.875 12.7716 8.875H11.7174C11.0547 8.875 10.4909 9.3456 10.2819 10.0025"
        stroke="#3D3D3D"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.75 12.25C0.75 15.3 1.9616 18.2251 4.11827 20.3817C6.27494 22.5384 9.20001 23.75 12.25 23.75C15.3 23.75 18.2251 22.5384 20.3817 20.3817C22.5384 18.2251 23.75 15.3 23.75 12.25C23.75 9.20001 22.5384 6.27494 20.3817 4.11827C18.2251 1.9616 15.3 0.75 12.25 0.75C9.20001 0.75 6.27494 1.9616 4.11827 4.11827C1.9616 6.27494 0.75 9.20001 0.75 12.25Z"
        stroke="#F0F4F7"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function ThirdStepRowIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden>
      <path
        d="M9.75 15.5C9.9867 16.2865 10.6254 16.85 11.3762 16.85H12.5257C13.478 16.85 14.25 15.9434 14.25 14.825V14.4875C14.25 13.3691 13.478 12.4625 12.5257 12.4625H11.9509H12.382C13.255 12.4625 13.9626 11.6314 13.9626 10.6063C13.9626 9.58106 13.255 8.74999 12.382 8.75L11.5199 8.75001C10.7964 8.75001 10.1864 9.32089 9.99877 10.1"
        stroke="#3D3D3D"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.75 12.25C0.75 15.3 1.9616 18.2251 4.11827 20.3817C6.27494 22.5384 9.20001 23.75 12.25 23.75C15.3 23.75 18.2251 22.5384 20.3817 20.3817C22.5384 18.2251 23.75 15.3 23.75 12.25C23.75 9.20001 22.5384 6.27494 20.3817 4.11827C18.2251 1.9616 15.3 0.75 12.25 0.75C9.20001 0.75 6.27494 1.9616 4.11827 4.11827C1.9616 6.27494 0.75 9.20001 0.75 12.25Z"
        stroke="#F0F4F7"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function FourthStepRowIcon() {
  return (
    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.875 16.0508V7.55078L9 14.4317H15" stroke="#3D3D3D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M0.75 12.25C0.75 15.3 1.9616 18.2251 4.11827 20.3817C6.27494 22.5384 9.20001 23.75 12.25 23.75C15.3 23.75 18.2251 22.5384 20.3817 20.3817C22.5384 18.2251 23.75 15.3 23.75 12.25C23.75 9.20001 22.5384 6.27494 20.3817 4.11827C18.2251 1.9616 15.3 0.75 12.25 0.75C9.20001 0.75 6.27494 1.9616 4.11827 4.11827C1.9616 6.27494 0.75 9.20001 0.75 12.25Z" stroke="#F0F4F7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function GenericStepRowIcon({ index }: { index: number }) {
  return (
    <div
      className="relative flex h-[25px] w-[25px] shrink-0 items-center justify-center"
      aria-hidden
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="25"
        viewBox="0 0 25 25"
        fill="none"
        className="absolute inset-0"
      >
        <path
          d="M0.75 12.25C0.75 15.3 1.9616 18.2251 4.11827 20.3817C6.27494 22.5384 9.20001 23.75 12.25 23.75C15.3 23.75 18.2251 22.5384 20.3817 20.3817C22.5384 18.2251 23.75 15.3 23.75 12.25C23.75 9.20001 22.5384 6.27494 20.3817 4.11827C18.2251 1.9616 15.3 0.75 12.25 0.75C9.20001 0.75 6.27494 1.9616 4.11827 4.11827C1.9616 6.27494 0.75 9.20001 0.75 12.25Z"
          stroke="#F0F4F7"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="relative text-[13px] font-[500] leading-none text-[#3D3D3D]">{index}</span>
    </div>
  )
}
